import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND = {
  expired: { label: 'Süresi Doldu', cls: 'bg-rose-100 text-rose-700' },
  rejected: { label: 'Reddedildi', cls: 'bg-rose-100 text-rose-700' },
  missing: { label: 'Eksik', cls: 'bg-amber-100 text-amber-700' },
  expiring: { label: 'Süresi Yaklaşıyor', cls: 'bg-orange-100 text-orange-700' },
};

const fmt = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '');

const ORDER = ['expired', 'rejected', 'missing', 'expiring'];
const detailOf = (a) => a.kind === 'expiring' ? `${a.days_left} gün kaldı · ${fmt(a.validity)}` : a.kind === 'expired' ? `Bitiş: ${fmt(a.validity)}` : a.note ? `Neden: ${a.note}` : '';

// Personele göre gruplar; süresi dolan belgesi olan personel en üstte
const groupByEmployee = (items) => {
  const map = new Map();
  items.forEach(a => { if (!map.has(a.employee_id)) map.set(a.employee_id, { id: a.employee_id, name: a.employee_name, docs: [] }); map.get(a.employee_id).docs.push(a); });
  const rank = (g) => Math.min(...g.docs.map(d => ORDER.indexOf(d.kind)));
  return [...map.values()].map(g => ({ ...g, docs: g.docs.sort((x, y) => ORDER.indexOf(x.kind) - ORDER.indexOf(y.kind)) }))
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'tr'));
};

const EmployeeRow = ({ g, onGo }) => (
  <li className="px-4 py-3" data-testid={`firm-doc-alert-emp-${g.id}`}>
    <div className="flex items-center gap-3">
      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{g.name} <span className="font-normal text-slate-400">· {g.docs.length} belge</span></p>
      <button type="button" onClick={onGo} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-700 hover:underline" data-testid="firm-doc-alert-go">
        Personele Git <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
    <ul className="mt-2 space-y-1.5">
      {g.docs.map(a => (
        <li key={a.doc_key} className="flex items-start gap-2 pl-1 text-xs sm:pl-3" data-testid={`firm-doc-alert-${a.employee_id}-${a.doc_key}`}>
          <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ${KIND[a.kind].cls}`} data-testid="firm-doc-alert-status">{KIND[a.kind].label}</span>
          <span className="min-w-0 break-words leading-5 text-slate-600">{a.doc_label}{detailOf(a) && ` · ${detailOf(a)}`}</span>
        </li>
      ))}
    </ul>
  </li>
);

export const EmployeeDocAlerts = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/firm/employee-doc-alerts`).then(res => setData(res.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const { items, warn_days: warnDays } = data;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="firm-doc-alerts">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`h-5 w-5 ${items.length ? 'text-amber-500' : 'text-slate-300'}`} />
          <h2 className="font-semibold text-slate-900">Personel Bazında Eksik / Süresi Dolan Belgeler</h2>
        </div>
        <span className="text-xs text-slate-500" data-testid="firm-doc-alerts-count">{new Set(items.map(a => a.employee_id)).size} personel · {items.length} belge · {warnDays} gün eşiği</span>
      </div>
      {items.length === 0 ? (
        <p className="flex items-center gap-2 px-4 py-4 text-sm text-emerald-700" data-testid="firm-doc-alerts-empty">
          <CheckCircle2 className="h-4 w-4" />Tüm personel belgeleriniz güncel.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-[32rem] overflow-y-auto">
          {groupByEmployee(items).map(g => <EmployeeRow key={g.id} g={g} onGo={() => navigate(`/employees?q=${encodeURIComponent(g.name)}`)} />)}
        </ul>
      )}
    </div>
  );
};
